import React, {useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import MutationStatusMessage from "../../../components/MutationStatusMessage";
import PageHeader from "../../../components/PageHeader";
import TextInput from "../../../components/TextInput";
import {useMutation, useQueryClient} from "react-query";
import {apollo} from "../../../utils/apollo";
import {useNavigate} from "react-router-dom";
import {CREATE_SUB_VENDOR} from "../../../utils/graphql";
import Toggle from "../../../components/Toggle";
import Button from "../../../components/Button";
import {toast} from "react-toastify";
import {UserIcon, BanknotesIcon, BuildingLibraryIcon} from '@heroicons/react/24/outline';
import {useTranslation} from "react-i18next";

function SubVendorCreate() {
    const {t} = useTranslation();
    const [name, setName] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [sharePercentage, setSharePercentage] = useState('20');
    const [displaySupplierDetails, setDisplaySupplierDetails] = useState(true);
    const [bankAccountHolderName, setBankAccountHolderName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [iban, setIban] = useState('');
    const queryClient = useQueryClient()
    const navigate = useNavigate();

    const createSubVendorMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: CREATE_SUB_VENDOR, variables: {
                    input: {
                        name,
                        businessName,
                        email,
                        phone,
                        displaySupplierDetails,
                        sharePercentage,
                        bank_account_holder_name: bankAccountHolderName,
                        bank_account: bankAccount,
                        iban,
                    }
                },
            });
            return data?.createSubVendor;
        }, onSuccess: () => {
            queryClient.invalidateQueries(['suppliers_list']).then(() => {
                setTimeout(() => navigate("/merchant/sub-vendors"), 2000);
            });
        },
    })

    const handleSubVendorForm = () => {
        if (!name) {
            toast.error(t('sub_vendors.create.validation.name_required'))
            return
        }
        if (!businessName) {
            toast.error(t('sub_vendors.create.validation.business_name_required'))
            return
        }
        if (!email) {
            toast.error(t('sub_vendors.create.validation.email_required'))
            return
        }
        if (!phone) {
            toast.error(t('sub_vendors.create.validation.phone_required'))
            return
        }
        if (!sharePercentage || isNaN(Number(sharePercentage)) || Number(sharePercentage) < 1 || Number(sharePercentage) > 99) {
            toast.error(t('sub_vendors.create.validation.share_percentage_invalid'));
            return;
        }
        createSubVendorMutation.mutate()
    }

    return (
        <DashboardLayout>
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-blue-500 shadow-sm">
                        <UserIcon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('sub_vendors.create.page_title')}</h2>
                </div>
                <p className="text-gray-600 mt-1">{t('sub_vendors.create.page_description')}</p>
            </div>

            <MutationStatusMessage mutation={createSubVendorMutation}/>

            {/* Basic Information Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-white/20">
                            <UserIcon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-medium text-white">{t('sub_vendors.create.basic_information')}</h3>
                    </div>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="name" 
                                label={t('sub_vendors.table.supplierName')} 
                                id="name" 
                                type="text" 
                                get={name} 
                                set={setName} 
                                required={true}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.name_description')}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput
                                name="sharePercentage"
                                label={t('sub_vendors.create.share_percentage')}
                                id="sharePercentage"
                                type="number"
                                get={sharePercentage}
                                set={setSharePercentage}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">
                                {t('sub_vendors.create.share_percentage_description')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Supplier Details Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-white/20">
                            <BanknotesIcon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-medium text-white">{t('sub_vendors.create.supplier_details')}</h3>
                    </div>
                </div>
                <div className="p-6">
                    <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg border border-yellow-200 mb-6">
                        <p>
                            {t('sub_vendors.create.supplier_details_description')}
                        </p>
                    </div>

                    <div className="mb-6">
                        <Toggle get={displaySupplierDetails} set={setDisplaySupplierDetails}>
                            {t('sub_vendors.create.display_supplier_details')}
                        </Toggle>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('sub_vendors.create.display_supplier_details_description')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="businessName" 
                                label={t('sub_vendors.table.businessName')} 
                                id="businessName" 
                                type="text"
                                get={businessName} 
                                set={setBusinessName} 
                                required={true}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.business_name_example')}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="email" 
                                label={t('sub_vendors.table.email')} 
                                id="email" 
                                type="text" 
                                get={email} 
                                set={setEmail}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.email_example')}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="phone" 
                                label={t('sub_vendors.table.phone')} 
                                id="phone" 
                                type="text" 
                                get={phone} 
                                set={setPhone}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.phone_example')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bank Details Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-white/20">
                            <BuildingLibraryIcon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-medium text-white">{t('sub_vendors.create.bank_details')}</h3>
                    </div>
                </div>
                <div className="p-6">
                    <div className="bg-blue-100 text-blue-800 p-4 rounded-lg border border-blue-200 mb-6">
                        <p>
                            {t('sub_vendors.create.bank_details_description')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="bankAccountHolderName" 
                                label={t('sub_vendors.create.bank_account_holder_name')} 
                                id="bankAccountHolderName" 
                                type="text" 
                                get={bankAccountHolderName} 
                                set={setBankAccountHolderName}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.bank_account_holder_name_example')}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="bankAccount" 
                                label={t('sub_vendors.create.bank_account')} 
                                id="bankAccount" 
                                type="text" 
                                get={bankAccount} 
                                set={setBankAccount}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.bank_account_example')}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <TextInput 
                                name="iban" 
                                label={t('sub_vendors.create.iban')} 
                                id="iban" 
                                type="text" 
                                get={iban} 
                                set={setIban}
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500 mt-2">{t('sub_vendors.create.iban_example')}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4">
                <Button 
                    type="button" 
                    onClick={handleSubVendorForm}
                >
                    {t('sub_vendors.create.create_button')}
                </Button>
                <Button 
                    type="button"
                    secondary={true}
                    onClick={() => navigate('/merchant/sub-vendors')}
                >
                    {t('sub_vendors.create.cancel_button')}
                </Button>
            </div>
        </DashboardLayout>
    );
}

export default SubVendorCreate;
